import { getAccessBasedRoute } from "services/utils/accessedBasedRoute";

// Known backend `sub_module_name` data typos/variants that should still be
// treated as the canonical screen name below. Every lookup here is an exact
// string match, so a spelling/casing difference between what a screen asks
// for and what the provisioning API actually returns reads as "not granted"
// even when it was granted -- these entries are the confirmed-safe fixes
// for that, not guesses:
//  - "Attendance Correction" -> the catalog has a live data-entry typo,
//    "Attendace Correction" (missing the "n"), for at least the Strco
//    tenant (sub_module_id 317).
//  - "Summarized working hours"/"Detailed working hours" -> the Reports
//    registry (reportRegistry.js) spells these in sentence case while the
//    live payload returns Title Case ("Summarized Working Hours" /
//    "Detailed Working Hours").
//  - "Leave and Permission Reports" -> the registry's plural doesn't match
//    the payload's singular "Leave and Permission Report".
//  - "Time card" -> the registry's short label vs. the payload's full
//    "Time Card Report".
//  - WorkingHours/index.js derives its own `moduleName` independently of
//    reportRegistry.js (it picks a name per selected report type, e.g.
//    "Summarized Working Hours" / "Detailed Working Hours" / "Time Card
//    Report") and passes THAT literal string as `screen` to
//    checkViewerPermission for the in-screen download/save-view gate --
//    a third spelling on top of the registry's sentence-case one, and
//    neither matches the live payload's "Working Hours Summary" /
//    "Working Hours Detail" / "Time Card". Without its own alias entries
//    here, the Reports hub list would still show these (its gate goes
//    through the "Summarized working hours"/"Time card" keys below) while
//    the screen's own Download/Save View controls throw access denied.
// Safety net only: the backend values should still be corrected/aligned,
// at which point these entries become no-ops rather than something to
// remove.
const SUB_MODULE_NAME_ALIASES = {
  // My Request > Comp Off: spelling not confirmed against a live payload, so
  // accept the common variants; the same lookup gates the sidebar row, the
  // Apply screen tab and the Home "My Requests Summary" card row.
  "Comp Off": [
    "Comp Off",
    "Comp off",
    "Compoff",
    "Comp-Off",
    "CompOff",
    "Comp Off Request",
    "Myzone Comp Off",
  ],
  "Working Hours Report": ["Working Hours Report", "Working Hours"],
  "Attendance Correction": ["Attendance Correction", "Attendace Correction"],
  "Summarized working hours": [
    "Summarized working hours",
    "Summarized Working Hours",
    "Working Hours Summary",
  ],
  "Detailed working hours": [
    "Detailed working hours",
    "Detailed Working Hours",
    "Working Hours Detail",
  ],
  "Summarized Working Hours": [
    "Summarized Working Hours",
    "Working Hours Summary",
  ],
  "Detailed Working Hours": ["Detailed Working Hours", "Working Hours Detail"],
  "Time Card Report": ["Time Card Report", "Time Card"],
  "Leave and Permission Reports": [
    "Leave and Permission Reports",
    "Leave and Permission Report",
    "Leave and Permission",
  ],
  "Time card": ["Time card", "Time Card Report", "Time Card"],
  // Punch Entry and Adjustment Entry are rendered by the shared Punch Details
  // report, but tenants may provision either the legacy shared name or the
  // mode-specific report name.
  "Punch Details": ["Punch Details", "Punch Entry", "Adjustment Entry"],
  "Punch Entry": ["Punch Entry", "Punch Details"],
  "Adjustment Entry": ["Adjustment Entry", "Punch Details"],
  "Overtime Report": ["Overtime Report", "Overtime Analysis"],
  "Attendance Anomalies Report": [
    "Attendance Anomalies Report",
    "Attendance Anomalies",
  ],
  Break: ["Break", "Break Report"],
  "Employee Attendance Report": [
    "Employee Attendance Report",
    "Employee Attendance",
  ],
  "Device Status Report": ["Device Status Report", "Device Status"],
  // Backend name not confirmed yet; accept the likely variants.
  "Punch Processing Exceptions": [
    "Punch Processing Exceptions",
    "Punch Processing Exceptions Report",
    "Punch Exceptions",
  ],
  "Workforce Status Report": ["Workforce Status Report", "Workforce Status"],
  "EOD Report": [
    "EOD Report",
    "Daily Report",
    "Daily Reports",
    "Employee Daily Report",
  ],
  Leaves: ["Leaves", "Leave Requests", "Leave Request"],
  Expense: ["Expense", "Expense Claims"],
  "Advance Salary": ["Advance Salary", "Salary Advance"],
  Permissions: ["Permissions", "Permission"],
  "Schedule Change": ["Schedule Change", "Shift Change"],
  "Myzone Off Day Duty": ["Myzone Off Day Duty", "Off Day Duty"],
  // People > Settings' real provisioning payload provisions this submodule
  // as "Check In Policy" (capital I); the screens under
  // TeamZone/Settings/CheckinPolicy were written against "Check in
  // Policy" (lowercase i) before that payload was checked.
  "Check in Policy": ["Check in Policy", "Check In Policy"],
};

// Every sub_module_name lookup in this file goes through here instead of a
// direct === comparison, so a screen name gets the alias treatment above
// everywhere it's checked (visibility, viewer/editor/approver permissions)
// rather than only wherever someone remembered to special-case it.
const findSubModule = (submodules, screen) => {
  if (!Array.isArray(submodules)) return undefined;
  const acceptedNames = SUB_MODULE_NAME_ALIASES[screen] ?? [screen];
  return submodules.find((sub) => acceptedNames.includes(sub?.sub_module_name));
};

export const findSubModuleByName = (productAccess, moduleName, screen) => {
  if (!Array.isArray(productAccess)) return undefined;

  const moduleAccess = productAccess.find(
    (module) => module?.module_name === moduleName,
  );
  if (!moduleAccess || !Array.isArray(moduleAccess.submodules))
    return undefined;

  return findSubModule(moduleAccess.submodules, screen);
};

export const findSubModuleAccess = (productAccess, moduleName, screen) => {
  return !!findSubModuleByName(productAccess, moduleName, screen);
};

export const checkViewerPermission = (productAccess, moduleName, screen) => {
  if (!Array.isArray(productAccess)) return false;

  const mod = productAccess.find((m) => m?.module_name === moduleName);
  if (!mod || !Array.isArray(mod.submodules)) return false;

  const sub = findSubModule(mod.submodules, screen);
  if (!sub) return false;
  return sub.permissions?.viewer === true;
};

export const checkEditorPermission = (productAccess, moduleName, screen) => {
  if (!Array.isArray(productAccess)) return false;

  const mod = productAccess.find((m) => m?.module_name === moduleName);
  if (!mod || !Array.isArray(mod.submodules)) return false;

  const sub = findSubModule(mod.submodules, screen);
  if (!sub) return false;
  return sub.permissions?.editor === true;
};

// For a shared edit surface reachable from more than one screen -- e.g.
// TeamZone/Attendance's EditCheckInOut modal, opened from Employee View,
// Department View, Shift View or Timeline View alike, with no prop telling
// it which one -- granting the edit action if ANY of those submodules has
// editor:true for this role is more accurate than picking one arbitrarily
// (a role could easily have editor rights on Shift View but not Employee
// View, and still expect to edit a record from either).
export const checkEditorPermissionAny = (productAccess, moduleName, screens) =>
  screens.some((screen) =>
    checkEditorPermission(productAccess, moduleName, screen),
  );

export const checkFirstLevelApproverPermission = (
  productAccess,
  moduleName,
  screen,
) => {
  if (!Array.isArray(productAccess)) return false;

  const mod = productAccess.find((m) => m?.module_name === moduleName);
  if (!mod || !Array.isArray(mod.submodules)) return false;

  const sub = findSubModule(mod.submodules, screen);
  if (!sub) return false;
  return sub.permissions?.first_level_approver === true;
};

export const checkSecondLevelApproverPermission = (
  productAccess,
  moduleName,
  screen,
) => {
  if (!Array.isArray(productAccess)) return false;

  const mod = productAccess.find((m) => m?.module_name === moduleName);
  if (!mod || !Array.isArray(mod.submodules)) return false;

  const sub = findSubModule(mod.submodules, screen);
  if (!sub) return false;
  return sub.permissions?.second_level_approver === true;
};

// Exported so Sidebar/index.js's APPROVE_CATEGORY_SUBMODULE and
// TeamZone/Approve/index.js's ITEM_KEY_SUBMODULE -- both of which map a
// display name to a canonical sub_module_name and then test membership in
// a submodules array directly, rather than calling the check*Permission
// helpers above -- share the exact same alias table instead of
// re-declaring their own copy that could drift out of sync with this one.
export const subModuleNameMatches = (subModuleName, screen) => {
  const acceptedNames = SUB_MODULE_NAME_ALIASES[screen] ?? [screen];
  return acceptedNames.includes(subModuleName);
};

// Redux holds one product's provisioning response at a time. During navigation
// it may still contain the previous product until the sidebar's request resolves.
// A redirect guard must wait for the requested product, including when the
// previous response came from localStorage or a cached sidebar request.
export const isProvisioningLoaded = (userScreenAccess, expectedProduct) =>
  userScreenAccess != null &&
  typeof userScreenAccess === "object" &&
  !Array.isArray(userScreenAccess) &&
  (!expectedProduct || userScreenAccess.product_name === expectedProduct);

// Administration's own submenu rows, in the same order as routes.js/
// Sidebar, each paired with the module_name its own screen's
// checkViewerPermission call already uses. "Roles & User Group" checks the
// module as a whole (either Roles or User Group having viewer:true is
// enough), matching how Sidebar's own ADMINISTRATION_VIEWER_GATED_MODULES
// decides whether that link shows at all -- not which of its two tabs.
const ADMINISTRATION_ROUTES = [
  { path: "/admin/administration/companies", moduleName: "Companies" },
  { path: "/admin/administration/sites", moduleName: "Sites" },
  {
    path: "/admin/administration/role/usergroup",
    moduleName: "Roles & User Group",
  },
  { path: "/admin/administration/user", moduleName: "Users" },
  {
    path: "/admin/administration/notification-settings",
    moduleName: "Notification Settings",
  },
];

// Used when a user lands on (or is refreshed into) an Administration screen
// they no longer have viewer access to -- instead of a dead-end "Access
// Restricted" page, redirect to whichever of Administration's own screens
// they can actually see.
export const getFirstAccessibleAdministrationPath = (moduleDetails) => {
  const accessible = ADMINISTRATION_ROUTES.find(({ moduleName }) => {
    const mod = moduleDetails?.find((m) => m?.module_name === moduleName);
    return !!mod?.submodules?.some((sub) => sub?.permissions?.viewer === true);
  });
  return accessible?.path ?? null;
};

// Full fallback used by every Administration screen's redirect-away effect:
// try another Administration screen first, and only if literally none of
// them are viewable, fall back to the same "first accessible product"
// mechanism the login flow already uses (TenantDetails.js/Login/index.js)
// via getAccessBasedRoute + the productAccess list login stores in
// localStorage. "Administration" is excluded from that product list on
// purpose -- we already know from the check above that none of its own
// screens are accessible, so picking it again here would bounce the user
// straight back into another dead end.
export const getAdministrationRedirectPath = (moduleDetails) => {
  const accessibleAdminPath =
    getFirstAccessibleAdministrationPath(moduleDetails);
  if (accessibleAdminPath) return accessibleAdminPath;

  try {
    const accessibleProducts = JSON.parse(
      localStorage.getItem("productAccess") || "null",
    );
    const fallbackProduct = accessibleProducts?.find(
      (product) =>
        product?.product_name && product.product_name !== "Administration",
    )?.product_name;
    return fallbackProduct
      ? getAccessBasedRoute(fallbackProduct)
      : "/admin/myzone/status";
  } catch (error) {
    return "/admin/myzone/status";
  }
};
